import React, { useEffect, useState } from "react";
import Navitems from "../NavItems/Nav_items";
import { BiLogOut } from "react-icons/bi";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { FaUserSecret } from "react-icons/fa";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdLockOutline,
} from "react-icons/md";
import { RiAdminLine } from "react-icons/ri";

function Sidebar({
  setShowSidebar,
  showSidebar,
  setShowCancelButton,
  show,
  setShow,
}) {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Read the admin email/name from cookie if available
  const adminEmail = Cookies.get("adminEmail") || "";
  const adminName = Cookies.get("adminName") || "Admin";
  const initials = adminName.slice(0, 2).toUpperCase();

  useEffect(() => {
    if (show === true) {
      let t = setTimeout(() => setShowCancelButton(true), 900);
      return () => clearTimeout(t);
    }
  }, [show]);

  const handleLogout = () => {
    Cookies.remove("adminToken");
    Cookies.remove("adminEmail");
    Cookies.remove("adminName");
    navigate("/login");
  };

  const isCollapsed = showSidebar; // alias for clarity

  return (
    <div className="bg-sidebar flex flex-col h-full w-full overflow-hidden">

      {/* ── Collapse toggle (desktop only) ── */}
      <div className="hidden laptopM:flex justify-end px-2 pt-3">
        {isCollapsed ? (
          <button
            onClick={() => setShowSidebar(false)}
            title="Expand sidebar"
            className="p-2 rounded-lg bg-sidebarHover hover:bg-secondMainColor
              text-slate-400 hover:text-white transition-all"
          >
            <MdKeyboardArrowRight className="text-lg" />
          </button>
        ) : (
          <button
            onClick={() => setShowSidebar(true)}
            title="Collapse sidebar"
            className="p-2 rounded-lg bg-sidebarHover hover:bg-secondMainColor
              text-slate-400 hover:text-white transition-all"
          >
            <MdKeyboardArrowLeft className="text-lg" />
          </button>
        )}
      </div>

      {/* ── Brand header ── */}
      {(!isCollapsed || show) && (
        <div className={`px-5 ${isCollapsed ? "py-3" : "pt-5 pb-4"}`}>
          <span className="text-white font-bold text-xl tracking-tight block">
            Kraken
          </span>
          <p className="text-slate-400 text-xs mt-0.5">Admin Dashboard</p>
        </div>
      )}
      {(!isCollapsed || show) && (
        <div className="h-px bg-slate-700/50 mx-4 mb-2" />
      )}

      {/* ── Nav items (scrollable) ── */}
      <div className="flex-1 overflow-y-auto py-1">
        <Navitems
          show={show}
          setShow={setShow}
          showSidebar={showSidebar}
          setShowSidebar={setShowSidebar}
          setShowCancelButton={setShowCancelButton}
        />

        {/* Create Admin link */}
        <div
          className={`mx-2 my-1 font-medium cursor-pointer rounded-lg flex gap-3 items-center
            py-2.5 px-3 hover:bg-sidebarHover text-slate-400 hover:text-white transition-colors
            ${isCollapsed ? "justify-center" : ""}`}
          onClick={() => {
            navigate("/create_admin_account");
            setShow(false);
          }}
          title="Create admin"
        >
          <FaUserSecret className="text-lg text-violet-400 flex-shrink-0" />
          {(!isCollapsed || show) && (
            <span className="text-sm">Create Admin</span>
          )}
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="h-px bg-slate-700/50 mx-4" />

      {/* ── Profile footer ── */}
      <div className="p-3 space-y-1">

        {/* Profile card — opens quick menu */}
        <div
          className={`relative flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer
            hover:bg-sidebarHover transition-colors select-none
            ${isCollapsed ? "justify-center" : ""}`}
          onClick={() => setShowProfileMenu((v) => !v)}
          title="Admin profile"
        >
          {/* Avatar */}
          <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700
            flex items-center justify-center text-white text-xs font-bold">
            {initials || <RiAdminLine className="text-base" />}
          </div>

          {/* Name + email */}
          {(!isCollapsed || show) && (
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold truncate">{adminName}</div>
              {adminEmail && (
                <div className="text-slate-500 text-xs truncate">{adminEmail}</div>
              )}
              {!adminEmail && (
                <div className="text-slate-500 text-xs">Administrator</div>
              )}
            </div>
          )}

          {(!isCollapsed || show) && (
            <MdKeyboardArrowRight
              className={`flex-shrink-0 text-slate-500 text-lg transition-transform duration-200
                ${showProfileMenu ? "rotate-90" : ""}`}
            />
          )}

          {/* Pop-up menu (shown when collapsed icon is clicked too) */}
          {showProfileMenu && (
            <div
              className={`absolute bottom-full mb-2 bg-white rounded-xl shadow-dropdown border border-slate-100
                py-1.5 z-[100] min-w-[200px]
                ${isCollapsed ? "left-14" : "left-0 right-0"}`}
            >
              {/* Update Password */}
              <button
                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5
                  hover:bg-violet-50 text-slate-700 hover:text-violet-700 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/update_password");
                  setShow(false);
                  setShowProfileMenu(false);
                }}
              >
                <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <MdLockOutline className="text-violet-600 text-sm" />
                </div>
                Update Password
              </button>

              <div className="mx-3 my-1 border-t border-slate-100" />

              {/* Logout */}
              <button
                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5
                  hover:bg-red-50 text-red-500 hover:text-red-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLogout();
                }}
              >
                <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <BiLogOut className="text-red-500 text-sm" />
                </div>
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Quick logout button (always visible) */}
        <button
          onClick={handleLogout}
          title="Logout"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
            hover:bg-red-500/15 text-slate-500 hover:text-red-400 transition-colors
            ${isCollapsed ? "justify-center" : ""}`}
        >
          <BiLogOut className="text-lg flex-shrink-0" />
          {(!isCollapsed || show) && (
            <span className="text-sm font-medium">Logout</span>
          )}
        </button>
      </div>

    </div>
  );
}

export default Sidebar;
